import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Calendar, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

const ALMERE_CENTER = [52.3680, 5.2200];
const ALMERE_ZOOM = 12;

const ActivityMap = () => {
  const { t, language } = useAuth();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedTime, setSelectedTime] = useState('');
  const [mapReady, setMapReady] = useState(false);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    const days = language === 'tr' ? ['Paz','Pzt','Sal','Çar','Per','Cum','Cmt'] :
                 language === 'pl' ? ['Nd','Pn','Wt','Śr','Cz','Pt','Sb'] :
                 language === 'en' ? ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'] :
                 ['Zo','Ma','Di','Wo','Do','Vr','Za'];
    const months = language === 'tr' ? ['Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'] :
                   language === 'pl' ? ['Sty','Lut','Mar','Kwi','Maj','Cze','Lip','Sie','Wrz','Paź','Lis','Gru'] :
                   language === 'en' ? ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'] :
                   ['Jan','Feb','Mrt','Apr','Mei','Jun','Jul','Aug','Sep','Okt','Nov','Dec'];
    return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`;
  };

  const changeDate = (offset) => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + offset);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  useEffect(() => {
    const fetchMapData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('date', selectedDate);
        if (selectedTime) params.append('time_from', selectedTime);
        const res = await api.get(`/activities/map?${params.toString()}`);
        setActivities(res.data || []);
      } catch (e) {
        console.error('Map data error:', e);
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMapData();
  }, [selectedDate, selectedTime]);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const L = window.L;
    if (!L) return;

    const map = L.map(mapRef.current, {
      center: ALMERE_CENTER, zoom: ALMERE_ZOOM, zoomControl: true,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap', maxZoom: 18,
    }).addTo(map);

    mapInstanceRef.current = map;
    markersRef.current = L.layerGroup().addTo(map);
    setMapReady(true);

    return () => { map.remove(); mapInstanceRef.current = null; };
  }, []);

  useEffect(() => {
    if (!mapReady || !markersRef.current) return;
    const L = window.L;
    if (!L) return;

    markersRef.current.clearLayers();
    activities.forEach((act) => {
      if (!act.lat || !act.lng) return;
      const isPga = act.source === 'pga';
      const color = isPga ? '#69a1f5' : '#fc8f78';
      const icon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="width:28px;height:28px;background:${color};border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;"><svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg></div>`,
        iconSize: [28, 28], iconAnchor: [14, 28], popupAnchor: [0, -28],
      });
      const timeStr = act.time ? `${act.time}${act.end_time ? ' - ' + act.end_time : ''}` : '';
      const esc = (s) => String(s ?? '').replace(/"/g, '&quot;').replace(/</g, '&lt;');
      const dataAttrs = `data-activity-name="${esc(act.title)}" data-activity-category="${esc(act.activity_type || '')}"`;
      const popup = `<div ${dataAttrs} style="min-width:200px;font-family:Ubuntu,sans-serif;"><h3 style="margin:0 0 4px;font-size:14px;font-weight:700;">${act.title}</h3><div style="font-size:12px;color:#666;margin-bottom:6px;">${act.organization_name ? act.organization_name + '<br>' : ''}${timeStr ? '🕐 ' + timeStr + '<br>' : ''}${act.location ? '📍 ' + act.location.substring(0, 50) : ''}</div><a href="/activity/${act.id}" ${dataAttrs} style="display:inline-block;padding:4px 12px;background:${color};color:${isPga ? 'white' : 'black'};border-radius:4px;text-decoration:none;font-size:12px;font-weight:600;">${t('activity.view')}</a></div>`;
      L.marker([act.lat, act.lng], { icon }).addTo(markersRef.current).bindPopup(popup);
    });
  }, [activities, mapReady, t]);

  const timeSlots = ['06:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00'];

  return (
    <div className="space-y-3" data-testid="activity-map">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 bg-white border border-gray-200 p-1">
          <Button variant="ghost" size="sm" onClick={() => changeDate(-1)} className="h-8 w-8 p-0"><ChevronLeft className="w-4 h-4" /></Button>
          <button onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])} className="px-3 py-1 text-sm font-heading font-bold text-primary min-w-[120px] text-center" data-testid="map-date-display">{formatDate(selectedDate)}</button>
          <Button variant="ghost" size="sm" onClick={() => changeDate(1)} className="h-8 w-8 p-0"><ChevronRight className="w-4 h-4" /></Button>
        </div>
        <select value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)} className="h-9 px-3 text-sm border border-gray-200 bg-white" data-testid="map-time-filter">
          <option value="">{language === 'tr' ? 'Tüm gün' : language === 'pl' ? 'Cały dzień' : language === 'en' ? 'All day' : 'Hele dag'}</option>
          {timeSlots.map(ts => (<option key={ts} value={ts}>{ts}</option>))}
        </select>
        <Badge className="bg-hoi-coral/10 text-hoi-coral border-0 font-medium">
          {loading ? '...' : `${activities.length} ${language === 'tr' ? 'etkinlik' : language === 'pl' ? 'aktywności' : language === 'en' ? 'activities' : 'activiteiten'}`}
        </Badge>
      </div>
      <div className="relative border border-gray-200 overflow-hidden" style={{ height: '500px' }}>
        <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
        {loading && (<div className="absolute inset-0 bg-white/50 flex items-center justify-center z-[1000]"><Loader2 className="w-8 h-8 animate-spin text-hoi-blue" /></div>)}
      </div>
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-hoi-coral inline-block" />{language === 'tr' ? 'Sakinler' : language === 'pl' ? 'Mieszkańcy' : language === 'en' ? 'Residents' : 'Inwoners zelf'}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-hoi-blue inline-block" />{language === 'tr' ? 'Kuruluşlar' : language === 'pl' ? 'Organizacje' : language === 'en' ? 'Organizations' : 'Organisaties'}</span>
      </div>
    </div>
  );
};

export default ActivityMap;
